import { TransformControls } from '@react-three/drei';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Euler, Object3D } from 'three';

import { usePlayerCameraControls, usePlayerStoreApi } from '@cartwhl/3d/player';

import { KEY_POSE_TRANSFORM_CONTROLS_SIZE } from '../constants';
import { useControlFrameAutoReconcile } from '../hooks/useControlFrameAutoReconcile';
import { useKeyPoseFrameCaptureActions } from '../hooks/useKeyPoseCaptureActions';
import { useKeyPoseIkEditing } from '../hooks/useKeyPoseIkEditing';
import { useKeyPosePersistenceActions } from '../hooks/useKeyPosePersistenceActions';
import {
  createEditableKeyPoseScene,
  solveKeyPoseIkTarget,
} from '../ik/keyPoseIkSystem';
import {
  clearKeyPoseDragPreview,
  setKeyPoseDragPreview,
} from '../keyPoseDragPreview';
import { useStore as useKeyPoseStore } from '../keyPoseStore';
import { createKeyPoseTimingLogger } from '../keyPoseTiming';
import type {
  KeyPoseEditingPreviewRecord,
  KeyPoseId,
  KeyPoseRecord,
  KeyPoseVector3Tuple,
} from '../types';

interface KeyPoseIkHandlesProps {
  sceneRotation?: Euler;
  onHandleDragStart?: () => void;
}

const GIZMO_POSITION_EPSILON = 1e-7;

export default function KeyPoseIkHandles({
  sceneRotation,
  onHandleDragStart,
}: KeyPoseIkHandlesProps) {
  const keyPoseStore = useKeyPoseStore();
  const playerStore = usePlayerStoreApi();
  const { cameraControls } = usePlayerCameraControls();
  const { saveCurrentKeyPoses } = useKeyPosePersistenceActions();
  const { ensureEditableKeyPoseAtCurrentFrame } =
    useKeyPoseFrameCaptureActions();
  const { reconcileControlFramesAfterEdit } = useControlFrameAutoReconcile();
  const {
    actorScene,
    effectorId,
    handlePosition,
    keyPose,
    keyPoseIsTransient,
    lockFeet,
    sourceBvh,
    targetPositions,
  } = useKeyPoseIkEditing({ sceneRotation });
  const handleRef = useRef<Object3D | null>(null);
  const latestSolvedRef = useRef<KeyPoseEditingPreviewRecord | null>(null);
  const pendingBaseKeyPoseRef = useRef<KeyPoseRecord | null>(null);
  const pendingStartPositionRef = useRef<KeyPoseVector3Tuple | null>(null);
  const pendingCameraEnabledRef = useRef<boolean | null>(null);
  const dragBaseKeyPoseRef = useRef<KeyPoseRecord | null>(null);
  const dragStartPositionRef = useRef<KeyPoseVector3Tuple | null>(null);
  const dragCreatedKeyPoseRef = useRef(false);
  const previousKeyPoseIdRef = useRef<string | null>(null);
  const isDraggingRef = useRef(false);
  const editableSceneRef = useRef<Object3D | null>(null);
  const [handleObject, setHandleObject] = useState<Object3D | null>(null);
  const [shadowPosition, setShadowPosition] =
    useState<KeyPoseVector3Tuple | null>(null);

  const setDragging = useCallback(
    (dragging: boolean, keyPoseId: KeyPoseId | null = null) => {
      isDraggingRef.current = dragging;
      keyPoseStore.setKeyPoseEditingDragging(dragging, keyPoseId);
      playerStore.setGizmoDragging(dragging);
    },
    [keyPoseStore, playerStore],
  );

  const setHandleRefs = useCallback((object: Object3D | null) => {
    handleRef.current = object;
    setHandleObject(object);
  }, []);

  const disableCameraControlsForPendingHandle = useCallback(() => {
    pendingCameraEnabledRef.current = null;
    if (!cameraControls) {
      return;
    }

    if ('enabled' in cameraControls) {
      pendingCameraEnabledRef.current = (
        cameraControls as { enabled: boolean }
      ).enabled;
      (cameraControls as { enabled: boolean }).enabled = false;
    }
    cameraControls.cancel?.();
    cameraControls.stop?.();
  }, [cameraControls]);

  const releasePendingHandleCamera = useCallback(() => {
    const previousCameraEnabled = pendingCameraEnabledRef.current;
    if (previousCameraEnabled === null) {
      return;
    }

    pendingCameraEnabledRef.current = null;
    if (!cameraControls || !('enabled' in cameraControls)) {
      return;
    }

    cameraControls.stop?.();
    (cameraControls as { enabled: boolean }).enabled = previousCameraEnabled;
  }, [cameraControls]);

  const clearPendingHandleDrag = useCallback(() => {
    pendingBaseKeyPoseRef.current = null;
    pendingStartPositionRef.current = null;
  }, []);

  useEffect(() => {
    const previousKeyPoseId = previousKeyPoseIdRef.current;
    const nextKeyPoseId = keyPose?.id ?? null;
    previousKeyPoseIdRef.current = nextKeyPoseId;

    if (previousKeyPoseId && previousKeyPoseId !== nextKeyPoseId) {
      keyPoseStore.clearKeyPoseEditingPreview(previousKeyPoseId);
    }
  }, [keyPose?.id, keyPoseStore]);

  useEffect(() => {
    if (isDraggingRef.current) {
      return;
    }

    const nextPosition = handlePosition ? clonePosition(handlePosition) : null;
    setShadowPosition((current) =>
      arePositionsEqual(current, nextPosition) ? current : nextPosition,
    );
    latestSolvedRef.current = null;
  }, [effectorId, handlePosition, keyPose?.id]);

  useEffect(() => {
    if (!handleRef.current || !shadowPosition) {
      return;
    }

    handleRef.current.position.set(
      shadowPosition[0],
      shadowPosition[1],
      shadowPosition[2],
    );
  }, [shadowPosition]);

  useEffect(() => {
    return () => {
      const hadPendingHandleDrag = pendingBaseKeyPoseRef.current !== null;
      clearPendingHandleDrag();
      if (hadPendingHandleDrag && !isDraggingRef.current) {
        releasePendingHandleCamera();
      }
      if (isDraggingRef.current) {
        setDragging(false);
      }
    };
  }, [clearPendingHandleDrag, releasePendingHandleCamera, setDragging]);

  const solveAtPosition = useCallback(
    (nextPosition: KeyPoseVector3Tuple) => {
      const baseKeyPose = dragBaseKeyPoseRef.current ?? keyPose;
      if (!baseKeyPose || !effectorId) {
        return null;
      }

      const solved = solveKeyPoseIkTarget({
        actorScene,
        bvh: sourceBvh,
        keyPose: baseKeyPose,
        sceneRotation,
        effectorId,
        targetWorldPosition: nextPosition,
        targetPositions,
        lockFeet,
        moveWholeCharacter: effectorId === 'pelvis' && !lockFeet,
        editableScene: editableSceneRef.current,
      });
      if (!solved) {
        return null;
      }

      latestSolvedRef.current = solved;
      if (isDraggingRef.current) {
        setKeyPoseDragPreview(baseKeyPose.id, solved);
        return solved;
      }

      keyPoseStore.setKeyPoseEditingPreview({
        keyPoseId: baseKeyPose.id,
        ...solved,
      });

      return solved;
    },
    [
      actorScene,
      effectorId,
      keyPose,
      keyPoseStore,
      sceneRotation,
      sourceBvh,
      targetPositions,
      lockFeet,
    ],
  );

  const startPendingDrag = useCallback(() => {
    if (!keyPose || !effectorId || !shadowPosition) {
      return;
    }

    pendingBaseKeyPoseRef.current = keyPose;
    pendingStartPositionRef.current = clonePosition(shadowPosition);
    latestSolvedRef.current = null;
    disableCameraControlsForPendingHandle();
  }, [
    effectorId,
    keyPose,
    disableCameraControlsForPendingHandle,
    shadowPosition,
  ]);

  const ensureDragStarted = useCallback(
    (nextPosition: KeyPoseVector3Tuple) => {
      if (isDraggingRef.current) {
        return true;
      }

      let baseKeyPose = pendingBaseKeyPoseRef.current;
      const startPosition = pendingStartPositionRef.current;
      if (
        !baseKeyPose ||
        !effectorId ||
        !startPosition ||
        arePositionsEqual(startPosition, nextPosition)
      ) {
        return false;
      }

      dragCreatedKeyPoseRef.current = false;
      if (keyPoseIsTransient) {
        const result = ensureEditableKeyPoseAtCurrentFrame();
        if (!result) {
          return false;
        }

        const persistedKeyPose =
          keyPoseStore.state.keyPosesById[result.keyPoseId];
        if (!persistedKeyPose) {
          return false;
        }

        baseKeyPose = persistedKeyPose;
        dragCreatedKeyPoseRef.current = result.created;
      }

      if (cameraControls) {
        playerStore.saveGizmoCameraSnapshot(cameraControls);
        if ('enabled' in cameraControls) {
          (cameraControls as { enabled: boolean }).enabled = false;
        }
        cameraControls.cancel?.();
        cameraControls.stop?.();
      }

      setDragging(true, baseKeyPose.id);
      onHandleDragStart?.();

      dragBaseKeyPoseRef.current = baseKeyPose;
      dragStartPositionRef.current = clonePosition(startPosition);
      latestSolvedRef.current = null;
      editableSceneRef.current = createEditableKeyPoseScene({
        actorScene,
        keyPose: baseKeyPose,
        sceneRotation,
      });

      clearPendingHandleDrag();
      pendingCameraEnabledRef.current = null;
      return true;
    },
    [
      actorScene,
      cameraControls,
      clearPendingHandleDrag,
      effectorId,
      ensureEditableKeyPoseAtCurrentFrame,
      keyPoseIsTransient,
      keyPoseStore,
      onHandleDragStart,
      playerStore,
      sceneRotation,
      setDragging,
    ],
  );

  const updateDrag = useCallback(() => {
    if (!handleRef.current || !effectorId) {
      return;
    }

    const nextPosition = getObjectPosition(handleRef.current);
    setShadowPosition(nextPosition);
    if (!ensureDragStarted(nextPosition)) {
      return;
    }

    solveAtPosition(nextPosition);
  }, [effectorId, ensureDragStarted, solveAtPosition]);

  const finishDrag = useCallback(() => {
    if (!isDraggingRef.current) {
      clearPendingHandleDrag();
      releasePendingHandleCamera();
      return;
    }

    const timing = createKeyPoseTimingLogger('ik.finishDrag', {
      effectorId,
      keyPoseId: dragBaseKeyPoseRef.current?.id ?? keyPose?.id ?? null,
      lockFeet,
    });

    try {
      if (cameraControls && 'enabled' in cameraControls) {
        (cameraControls as { enabled: boolean }).enabled = false;
        cameraControls.cancel?.();
        cameraControls.stop?.();
      }
      clearKeyPoseDragPreview();
      editableSceneRef.current = null;
      const baseKeyPose = dragBaseKeyPoseRef.current ?? keyPose;
      const nextPosition = handleRef.current
        ? getObjectPosition(handleRef.current)
        : null;
      const usedCachedSolution = latestSolvedRef.current !== null;
      const solved =
        latestSolvedRef.current ??
        (nextPosition ? solveAtPosition(nextPosition) : null);
      timing.mark('resolved final solve', {
        hasBaseKeyPose: Boolean(baseKeyPose),
        hasNextPosition: Boolean(nextPosition),
        hasSolvedPose: Boolean(solved),
        usedCachedSolution,
      });

      if (!baseKeyPose || !effectorId || !nextPosition || !solved) {
        if (baseKeyPose) {
          keyPoseStore.clearKeyPoseEditingPreview(baseKeyPose.id);
        }
        timing.mark('aborted finish');
        return;
      }

      const beforeAction = dragCreatedKeyPoseRef.current
        ? { keyPose: null, ikTargetPositions: null }
        : keyPoseStore.captureKeyPoseActionSnapshot(baseKeyPose.id);
      timing.mark('captured undo snapshot');
      const result = keyPoseStore.updateKeyPose({
        keyPoseId: baseKeyPose.id,
        ...solved,
      });
      timing.mark('updated key pose', {
        didWrite: Boolean(result?.didWrite),
        resultKeyPoseId: result?.keyPoseId ?? null,
      });
      if (!result?.didWrite) {
        keyPoseStore.clearKeyPoseEditingPreview(baseKeyPose.id);
        return;
      }

      const activeKeyPoseId = result.keyPoseId;

      if (effectorId === 'pelvis' && !lockFeet) {
        const delta = getPositionDelta(
          dragStartPositionRef.current,
          nextPosition,
        );
        keyPoseStore.translateKeyPoseIkTargetPositions({
          keyPoseId: activeKeyPoseId,
          delta,
        });
        timing.mark('translated stored IK targets', { delta });
      }

      keyPoseStore.setKeyPoseIkTargetPosition({
        keyPoseId: activeKeyPoseId,
        effectorId,
        position: nextPosition,
      });
      timing.mark('stored active IK target');
      keyPoseStore.clearKeyPoseEditingPreview(activeKeyPoseId);
      if (baseKeyPose.id !== activeKeyPoseId) {
        keyPoseStore.clearKeyPoseEditingPreview(baseKeyPose.id);
      }
      timing.mark('cleared editing previews');
      keyPoseStore.recordKeyPoseEditAction({
        keyPoseId: activeKeyPoseId,
        referenceName: result.referenceName,
        label: 'Edit key pose',
        before: beforeAction,
      });
      timing.mark('recorded edit action');
      void saveCurrentKeyPoses('edit-key-pose', {
        referenceName: result.referenceName,
      });
      timing.mark('queued key-pose save');
      reconcileControlFramesAfterEdit();
      timing.mark('queued control-frame reconcile');
    } finally {
      clearPendingHandleDrag();
      dragBaseKeyPoseRef.current = null;
      dragStartPositionRef.current = null;
      dragCreatedKeyPoseRef.current = false;
      latestSolvedRef.current = null;
      setDragging(false);
      timing.end('released dragging');
    }
  }, [
    cameraControls,
    clearPendingHandleDrag,
    effectorId,
    keyPose,
    keyPoseStore,
    reconcileControlFramesAfterEdit,
    releasePendingHandleCamera,
    saveCurrentKeyPoses,
    setDragging,
    solveAtPosition,
    lockFeet,
  ]);

  if (!actorScene || !sourceBvh || !keyPose || !effectorId || !shadowPosition) {
    return null;
  }

  return (
    <group>
      <group
        ref={setHandleRefs}
        position={shadowPosition}
        onPointerDown={(event) => {
          event.stopPropagation();
        }}
      />
      {handleObject ? (
        <TransformControls
          object={handleObject}
          mode="translate"
          size={KEY_POSE_TRANSFORM_CONTROLS_SIZE}
          onMouseDown={startPendingDrag}
          onMouseUp={finishDrag}
          onObjectChange={updateDrag}
        />
      ) : null}
    </group>
  );
}

function clonePosition(
  position: readonly [number, number, number],
): KeyPoseVector3Tuple {
  return [position[0], position[1], position[2]];
}

function getObjectPosition(object: Object3D): KeyPoseVector3Tuple {
  return [object.position.x, object.position.y, object.position.z];
}

function arePositionsEqual(
  left: KeyPoseVector3Tuple | null,
  right: KeyPoseVector3Tuple | null,
) {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return (
    areNumbersEqual(left[0], right[0]) &&
    areNumbersEqual(left[1], right[1]) &&
    areNumbersEqual(left[2], right[2])
  );
}

function areNumbersEqual(left: number, right: number) {
  return Math.abs(left - right) <= GIZMO_POSITION_EPSILON;
}

function getPositionDelta(
  startPosition: KeyPoseVector3Tuple | null,
  nextPosition: KeyPoseVector3Tuple,
): KeyPoseVector3Tuple {
  if (!startPosition) {
    return [0, 0, 0];
  }

  return [
    nextPosition[0] - startPosition[0],
    nextPosition[1] - startPosition[1],
    nextPosition[2] - startPosition[2],
  ];
}
